import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch,
  updateDoc,
  where,
  getDocs,
  limit,
  arrayUnion,
  deleteDoc,
} from 'firebase/firestore';

import { getFirestoreDb } from '../config/firebaseApp';
import type { ChatDoc, MessageDoc } from '../types/models';

const CHATS = 'chats';
const MESSAGES = 'messages';
const BATCH_DELETE_LIMIT = 450;

function normalizeDeletedBy(data: Record<string, unknown>): string[] | undefined {
  const raw = data.deletedBy;
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const arr = raw.filter((x): x is string => typeof x === 'string');
  return arr.length > 0 ? arr : undefined;
}

function mapChatDoc(id: string, data: Record<string, unknown>): ChatDoc {
  const participants = data.participants;
  const p: [string, string] =
    Array.isArray(participants) &&
    participants.length === 2 &&
    typeof participants[0] === 'string' &&
    typeof participants[1] === 'string'
      ? [participants[0], participants[1]]
      : ['', ''];
  return {
    id,
    participants: p,
    lastMessage: String(data.lastMessage ?? ''),
    lastMessageTime: data.lastMessageTime as ChatDoc['lastMessageTime'],
    deletedBy: normalizeDeletedBy(data),
  };
}

function mapMessageDoc(id: string, data: Record<string, unknown>): MessageDoc {
  return {
    id,
    senderId: String(data.senderId ?? ''),
    text: String(data.text ?? ''),
    timestamp: data.timestamp as MessageDoc['timestamp'],
    deletedBy: normalizeDeletedBy(data),
  };
}

function mapChatSnap(snap: Awaited<ReturnType<typeof getDocs>>): ChatDoc[] {
  const chats: ChatDoc[] = [];
  snap.forEach(d => {
    chats.push(mapChatDoc(d.id, d.data() as Record<string, unknown>));
  });
  return chats;
}

/**
 * Kullanıcının dahil olduğu sohbetler, `lastMessageTime` azalan sırada.
 * `deletedBy` içinde kullanıcı olanlar listeden çıkarılır.
 */
export async function listChatsForUser(uid: string, max = 100): Promise<ChatDoc[]> {
  const db = getFirestoreDb();
  let chats: ChatDoc[];
  try {
    const q = query(
      collection(db, CHATS),
      where('participants', 'array-contains', uid),
      orderBy('lastMessageTime', 'desc'),
      limit(max),
    );
    const snap = await getDocs(q);
    chats = mapChatSnap(snap);
  } catch {
    const q = query(
      collection(db, CHATS),
      where('participants', 'array-contains', uid),
      limit(max),
    );
    const snap = await getDocs(q);
    chats = mapChatSnap(snap);
    chats.sort((a, b) => b.lastMessageTime.toMillis() - a.lastMessageTime.toMillis());
  }
  return chats.filter(c => !c.deletedBy?.includes(uid));
}

/**
 * Mevcut sohbeti `array-contains` ile bulur (kurallar / liste sorgularıyla uyumlu); yoksa yeni belge oluşturur.
 * `uidA` = giriş yapan kullanıcı, `uidB` = karşı taraf.
 */
export async function ensureChat(
  uidA: string,
  uidB: string,
): Promise<{ chatId: string; chat: ChatDoc }> {
  if (!uidA?.trim() || !uidB?.trim() || uidA === uidB) {
    throw new Error('Geçersiz sohbet katılımcıları.');
  }

  const db = getFirestoreDb();
  const q = query(
    collection(db, CHATS),
    where('participants', 'array-contains', uidA),
    limit(200),
  );
  const querySnapshot = await getDocs(q);

  for (const d of querySnapshot.docs) {
    const data = d.data() as Record<string, unknown>;
    const raw = data.participants;
    if (!Array.isArray(raw)) {
      continue;
    }
    const participants = raw.filter((p): p is string => typeof p === 'string');
    if (participants.length === 2 && participants.includes(uidB)) {
      return { chatId: d.id, chat: mapChatDoc(d.id, data) };
    }
  }

  const chatRef = doc(collection(db, CHATS));
  const chatId = chatRef.id;
  const chatBody: Omit<ChatDoc, 'id'> = {
    participants: [uidA, uidB] as [string, string],
    lastMessage: '',
    lastMessageTime: Timestamp.now(),
    deletedBy: [],
  };
  await setDoc(chatRef, { id: chatId, ...chatBody });
  return { chatId, chat: { id: chatId, ...chatBody } };
}

/** Gerçek zamanlı mesaj dinleyicisi; `timestamp` artan sırada. */
export function subscribeMessages(
  chatId: string,
  viewerUid: string,
  onMessages: (items: MessageDoc[]) => void,
  onError?: (e: Error) => void,
) {
  const db = getFirestoreDb();
  const q = query(
    collection(doc(db, CHATS, chatId), MESSAGES),
    orderBy('timestamp', 'asc'),
  );
  return onSnapshot(
    q,
    snap => {
      const items: MessageDoc[] = [];
      snap.forEach(d => {
        const row = mapMessageDoc(d.id, d.data() as Record<string, unknown>);
        if (!row.deletedBy?.includes(viewerUid)) {
          items.push(row);
        }
      });
      onMessages(items);
    },
    err => onError?.(err as Error),
  );
}

/** Yeni mesaj + üst sohbet özeti güncellemesi (batch). */
export async function sendMessage(
  chatId: string,
  senderId: string,
  text: string,
): Promise<void> {
  const db = getFirestoreDb();
  const chatRef = doc(db, CHATS, chatId);
  const chatSnap = await getDoc(chatRef);
  if (!chatSnap.exists()) {
    throw new Error('Sohbet bulunamadı.');
  }
  const chatData = chatSnap.data() as Record<string, unknown>;
  const raw = chatData.participants;
  const participants = Array.isArray(raw)
    ? (raw as string[]).filter((p): p is string => typeof p === 'string')
    : [];
  if (participants.length !== 2 || !participants.includes(senderId)) {
    throw new Error('Bu sohbete mesaj gönderme iznin yok.');
  }

  const msgCol = collection(chatRef, MESSAGES);
  const batch = writeBatch(db);
  const msgRef = doc(msgCol);
  batch.set(msgRef, {
    senderId,
    text: text.trim(),
    timestamp: serverTimestamp(),
    deletedBy: [],
  });
  batch.update(chatRef, {
    lastMessage: text.trim(),
    lastMessageTime: serverTimestamp(),
  });
  await batch.commit();
}

/** Mesajı yalnızca bu kullanıcı için gizler. */
export async function deleteMessageForMe(
  chatId: string,
  messageId: string,
  userId: string,
): Promise<void> {
  const db = getFirestoreDb();
  const msgRef = doc(db, CHATS, chatId, MESSAGES, messageId);
  await updateDoc(msgRef, {
    deletedBy: arrayUnion(userId),
  });
}

/** Mesajı herkes için siler; yalnızca gönderen (kurallar + istemci). */
export async function deleteMessageForEveryone(
  chatId: string,
  messageId: string,
  senderId: string,
): Promise<void> {
  const db = getFirestoreDb();
  const msgRef = doc(db, CHATS, chatId, MESSAGES, messageId);
  const snap = await getDoc(msgRef);
  if (!snap.exists()) {
    return;
  }
  if (String(snap.data()?.senderId ?? '') !== senderId) {
    throw new Error('Bu mesajı herkesten yalnızca gönderen silebilir.');
  }
  await deleteDoc(msgRef);
}

/** Sohbeti bu kullanıcı için listeden gizler; her iki taraf da gizlediyse sohbet ve mesajlar silinir. */
export async function deleteConversationForMe(chatId: string, userId: string): Promise<void> {
  const db = getFirestoreDb();
  const chatRef = doc(db, CHATS, chatId);
  await updateDoc(chatRef, {
    deletedBy: arrayUnion(userId),
  });
  await tryPurgeChatIfBothSidesDeleted(chatId);
}

/**
 * İki katılımcı da `deletedBy` içindeyse tüm mesajları ve sohbet belgesini siler.
 */
export async function tryPurgeChatIfBothSidesDeleted(chatId: string): Promise<void> {
  const db = getFirestoreDb();
  const chatRef = doc(db, CHATS, chatId);
  const snap = await getDoc(chatRef);
  if (!snap.exists()) {
    return;
  }
  const data = snap.data() as Record<string, unknown>;
  const rawP = data.participants;
  const parts = Array.isArray(rawP)
    ? rawP.filter((p): p is string => typeof p === 'string')
    : [];
  const del = normalizeDeletedBy(data) ?? [];
  if (parts.length !== 2 || del.length < 2) {
    return;
  }
  const bothHidden = parts.every(p => del.includes(p));
  if (!bothHidden) {
    return;
  }

  const msgCol = collection(chatRef, MESSAGES);
  const all = await getDocs(msgCol);
  const refs = all.docs.map(d => d.ref);

  for (let i = 0; i < refs.length; i += BATCH_DELETE_LIMIT) {
    const chunk = refs.slice(i, i + BATCH_DELETE_LIMIT);
    const batch = writeBatch(db);
    chunk.forEach(r => batch.delete(r));
    await batch.commit();
  }

  await deleteDoc(chatRef);
}

/** @deprecated Yerine `sendMessage` kullanın */
export async function sendChatMessage(
  chatId: string,
  senderId: string,
  text: string,
): Promise<void> {
  return sendMessage(chatId, senderId, text);
}
